/* ==========================================================================
   dewy — marquee.js
   Duplicates the athlete track so the CSS scroll animation loops seamlessly.

   The markup lists each athlete ONCE; this doubles it at runtime. That means
   adding an athlete is a one-line markup change with no CSS to retune.

   Skipped entirely under prefers-reduced-motion, where the track becomes a
   manual snap-scroller and a duplicate would just repeat the images.

   Two things this handles that the first version didn't: a page with more than
   one marquee section (the section has a preset, so a merchant can add a
   second — the old querySelector only ever found the first, and the second
   one's loop visibly jumped), and adding an image block in the theme editor,
   which replaces the section and needs the new track doubling too.
   ========================================================================== */
(function () {
  'use strict';

  function fill(track) {
    // Already doubled — a second pass would quadruple it.
    if (track.dataset.marqueeFilled) return;
    track.dataset.marqueeFilled = '1';

    // cloneNode preserves already-decoded images; innerHTML += would re-parse
    // and re-fetch the whole set.
    var originals = Array.prototype.slice.call(track.children);
    if (!originals.length) return;

    var frag = document.createDocumentFragment();
    originals.forEach(function (node) {
      var copy = node.cloneNode(true);
      copy.setAttribute('aria-hidden', 'true'); // duplicates aren't new content
      frag.appendChild(copy);
    });
    track.appendChild(frag);
  }

  /* Native lazy loading does not work for this strip, and the reason is
     specific: the browser decides what to load from what intersects the
     VIEWPORT, but these images are moved into view by a CSS transform running
     horizontally inside an overflow-hidden box. On a phone that meant images 3
     onward never met the threshold at all — they stayed unloaded and animated
     past as blank gaps, while images 1 and 2 looked perfectly fine.

     So intersection is judged on the strip as a whole instead: the moment any
     part of it is on screen, every image in it is switched to eager, which
     starts the fetch immediately. Below-the-fold saving is kept — nothing
     loads until the section is actually approached. */
  function loadWhenNear(mq) {
    if (mq.dataset.marqueeArmed) return;
    mq.dataset.marqueeArmed = '1';

    function loadAll() {
      mq.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
        img.loading = 'eager';
      });
    }

    if (!('IntersectionObserver' in window)) {
      loadAll(); // older browser: correctness over the saving
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        loadAll();
        io.disconnect();
      });
    }, { rootMargin: '400px 0px' }); // start a little before it arrives

    io.observe(mq);
  }

  function init(root) {
    var scope = root || document;

    // Arming runs regardless of reduced motion — a still strip still needs
    // all of its images. Only the duplication is motion-dependent.
    scope.querySelectorAll('.mq').forEach(loadWhenNear);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    scope.querySelectorAll('.mq-track').forEach(fill);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });
})();
